"use client";
import RouterLink from "@/components/ui/router-link";
import isNewMark from "@/lib/isNewMark";
import { cn, downloadFile } from "@/lib/utils";
import {
  Cell,
  ColumnDef,
  Header,
  flexRender,
  getCoreRowModel,
  useReactTable,
} from "@tanstack/react-table";
import Datasheet from "../ProductDetails/Datasheet";

function TableHeader<T>({
  header,
  diffColumns,
}: {
  header: Header<T, unknown>;
  diffColumns: (ColumnDef<T, any> & {
    accessorKey?: string;
  })[];
}) {
  if (header.id === "material_number") {
    return <div className="h-32 border-b border-[#D3D3D3] bg-[#F7F7F7]"></div>;
  }

  return (
    <div
      className={cn("flex h-12 items-center border-b border-[#D3D3D3] pl-6", {
        "bg-[#F8FBFF]": diffColumns.some(
          (item) => item.accessorKey === header.id
        ),
      })}>
      {header.isPlaceholder
        ? null
        : flexRender(header.column.columnDef.header, header.getContext())}
    </div>
  );
}

function ProductCell<T>({ cell }: { cell: Cell<T, unknown> }) {
  const original = cell.row.original as {
    slug: string;
    material_number: string;
    is_new_product?: boolean;
    publish_date: string;
    datasheet1_file: string | null;
    datasheet2_file: string | null;
    datasheet3_file: string | null;
  };

  const datasheet = [
    original.datasheet1_file,
    original.datasheet2_file,
    original.datasheet3_file,
  ];

  return (
    <div className="flex h-32 flex-col justify-between border-b border-[#D3D3D3] bg-[#F7F7F7] px-6 pb-3 pt-5">
      <div className="flex">
        <RouterLink
          className="text-base text-primary transition-all duration-300 hover:underline"
          href={`/product/${original.slug}`}>
          {original.material_number}
        </RouterLink>

        {original?.is_new_product && (
          <div className="ml-2 md:text-sm text-xs text-[#FF5D2B]">NEW</div>
        )}
      </div>

      {datasheet.filter(Boolean).length > 0 && (
        <Datasheet datasheet={datasheet} name={original.material_number} />
      )}
    </div>
  );
}

export default function CompareTable<T>(props: {
  data: T[];
  columns: (ColumnDef<T, any> & {
    accessorKey?: string;
  })[];
  diffColumns: (ColumnDef<T, any> & {
    accessorKey?: string;
  })[];
}) {
  const table = useReactTable({
    data: props.data,
    columns: props.columns,
    getCoreRowModel: getCoreRowModel(),
  });

  return (
    <div className="relative mt-16">
      <div className="content_container mx-auto">
        <div className="relative flex overflow-x-auto">
          <div className="sticky left-0 flex min-w-[200px] flex-shrink-0 flex-col border border-b-0 border-[#D3D3D3] bg-white md:min-w-[240px]">
            {table.getHeaderGroups().map((headerGroup) => (
              <div className="w-full" key={headerGroup.id}>
                {headerGroup.headers.map((header) => (
                  <TableHeader
                    key={header.id}
                    header={header}
                    diffColumns={props.diffColumns}
                  />
                ))}
              </div>
            ))}
          </div>
          <div className="flex flex-1 border-t border-[#D3D3D3]">
            {table.getRowModel().rows.map((row) => (
              <div
                className="flex min-w-[300px] flex-1 flex-col border-r border-[#D3D3D3]"
                key={row.id}>
                {row.getVisibleCells().map((cell) => {
                  if (cell.column.id === "material_number") {
                    return <ProductCell key={cell.id} cell={cell} />;
                  }

                  return (
                    <div
                      key={cell.id}
                      className={cn(
                        "flex h-12 items-center border-b border-[#D3D3D3] pl-6",
                        {
                          "bg-[#F8FBFF]": props.diffColumns.some(
                            (item) => item.accessorKey === cell.column.id
                          ),
                        }
                      )}>
                      {flexRender(
                        cell.column.columnDef.cell,
                        cell.getContext()
                      )}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
